import { useEffect, useState } from 'react'
import { listInviteCodes, createInviteCode, revokeInviteCode } from '../../api/admin'
import type { ShopInviteCode } from '../../api/admin'

function StatusBadge({ status }: { status: ShopInviteCode['status'] }) {
  const colors: Record<ShopInviteCode['status'], string> = {
    ACTIVE: '#22c55e',
    USED: '#6b7280',
    REVOKED: '#ef4444',
  }
  return (
    <span
      style={{
        background: colors[status] + '22',
        color: colors[status],
        borderRadius: '6px',
        padding: '2px 8px',
        fontSize: '12px',
        fontWeight: 600,
      }}
    >
      {status}
    </span>
  )
}

function formatDate(iso: string | null) {
  if (!iso) return '—'
  return new Date(iso).toLocaleString()
}

export default function AdminInviteCodesPage() {
  const [codes, setCodes] = useState<ShopInviteCode[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [noteInput, setNoteInput] = useState('')
  const [creating, setCreating] = useState(false)
  const [showForm, setShowForm] = useState(false)
  const [copiedId, setCopiedId] = useState<string | null>(null)

  const load = async () => {
    setLoading(true)
    setError(null)
    try {
      setCodes(await listInviteCodes())
    } catch {
      setError('Failed to load invite codes')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [])

  const handleCreate = async () => {
    setCreating(true)
    try {
      const created = await createInviteCode(noteInput.trim() || undefined)
      setCodes([created, ...codes])
      setNoteInput('')
      setShowForm(false)
    } catch {
      setError('Failed to create invite code')
    } finally {
      setCreating(false)
    }
  }

  const handleRevoke = async (id: string) => {
    if (!confirm('Revoke this invite code?')) return
    try {
      await revokeInviteCode(id)
      setCodes(codes.map(c => c.id === id ? { ...c, status: 'REVOKED' as const } : c))
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Failed to revoke')
    }
  }

  const handleCopy = (code: string, id: string) => {
    navigator.clipboard.writeText(code)
    setCopiedId(id)
    setTimeout(() => setCopiedId(null), 1500)
  }

  const cardStyle = {
    background: 'var(--app-card-bg)',
    borderRadius: '12px',
    padding: '12px 16px',
  }

  const inputStyle = {
    background: 'var(--app-page-bg)',
    color: 'var(--tg-theme-text-color)',
    border: '1px solid transparent',
    borderRadius: '10px',
    padding: '9px 12px',
    fontSize: '15px',
    outline: 'none',
    width: '100%',
  }

  return (
    <div className="p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="font-bold text-base" style={{ color: 'var(--tg-theme-text-color)' }}>
          Shop Invite Codes
        </h2>
        <button
          onClick={() => setShowForm(v => !v)}
          style={{
            background: 'var(--tg-theme-button-color)',
            color: 'var(--tg-theme-button-text-color)',
            borderRadius: '10px',
            padding: '7px 14px',
            fontSize: '13px',
            fontWeight: 600,
          }}
        >
          + Create
        </button>
      </div>

      {/* Create form */}
      {showForm && (
        <div style={cardStyle} className="space-y-3">
          <p className="text-sm font-medium" style={{ color: 'var(--tg-theme-text-color)' }}>
            New invite code
          </p>
          <input
            style={inputStyle}
            placeholder="Note (optional — e.g. @username)"
            value={noteInput}
            onChange={e => setNoteInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && !creating && handleCreate()}
          />
          <div className="flex gap-2">
            <button
              onClick={handleCreate}
              disabled={creating}
              style={{
                background: 'var(--tg-theme-button-color)',
                color: 'var(--tg-theme-button-text-color)',
                borderRadius: '10px',
                padding: '8px 16px',
                fontSize: '13px',
                fontWeight: 600,
                opacity: creating ? 0.5 : 1,
              }}
            >
              {creating ? 'Creating…' : 'Generate'}
            </button>
            <button
              onClick={() => setShowForm(false)}
              style={{
                background: 'var(--app-page-bg)',
                color: 'var(--tg-theme-text-color)',
                borderRadius: '10px',
                padding: '8px 16px',
                fontSize: '13px',
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <p className="text-sm" style={{ color: '#ef4444' }}>{error}</p>
      )}

      {/* List */}
      {loading ? (
        <p className="text-sm opacity-50" style={{ color: 'var(--tg-theme-text-color)' }}>Loading…</p>
      ) : codes.length === 0 ? (
        <p className="text-sm opacity-50" style={{ color: 'var(--tg-theme-text-color)' }}>No invite codes yet.</p>
      ) : (
        <div className="space-y-2">
          {codes.map(c => (
            <div key={c.id} style={cardStyle} className="space-y-1.5">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span
                    className="font-mono font-bold text-base tracking-widest"
                    style={{ color: 'var(--tg-theme-text-color)' }}
                  >
                    {c.code}
                  </span>
                  <button
                    onClick={() => handleCopy(c.code, c.id)}
                    title="Copy code"
                    style={{
                      fontSize: '12px',
                      opacity: 0.6,
                      color: 'var(--tg-theme-text-color)',
                      background: 'var(--app-page-bg)',
                      borderRadius: '6px',
                      padding: '2px 7px',
                    }}
                  >
                    {copiedId === c.id ? '✓' : 'Copy'}
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  <StatusBadge status={c.status} />
                  {c.status === 'ACTIVE' && (
                    <button
                      onClick={() => handleRevoke(c.id)}
                      style={{
                        fontSize: '12px',
                        color: '#ef4444',
                        background: '#ef444422',
                        borderRadius: '6px',
                        padding: '2px 8px',
                        fontWeight: 600,
                      }}
                    >
                      Revoke
                    </button>
                  )}
                </div>
              </div>
              {c.note && (
                <p className="text-xs opacity-60" style={{ color: 'var(--tg-theme-text-color)' }}>
                  {c.note}
                </p>
              )}
              <div className="flex gap-4 text-xs opacity-40" style={{ color: 'var(--tg-theme-text-color)' }}>
                <span>Created: {formatDate(c.created_at)}</span>
                {c.used_at && <span>Used: {formatDate(c.used_at)}</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
